import { selectGpsTrackingProvider } from '@/features/domain/user'
import type { FormConfig } from '@workwave-tidal/core/form'
import { useMemo } from 'react'
import { useSelector } from 'react-redux'
import { Footer } from './components/Footer'
import { BarcodeAttachment } from './fields/BarcodeAttachment/BarcodeAttachment'
import { Email } from './fields/Email/Email'
import { MobileFutureDaysVisibility } from './fields/MobileFutureDaysVisibility/MobileFutureDaysVisibility'
import { Name } from './fields/Name/Name'
import { Password } from './fields/Password/Password'
import { SummarizeFutureMobileRoute } from './fields/SummarizeFutureMobileRoute/SummarizeFutureMobileRoute'
import { TrackDriver } from './fields/TrackDriver/TrackDriver'
import { emptyDriverId, form } from './formState'
import { useGpsDeviceIdOptions } from './hooks/useGpsDeviceIdOptions'
import { useOnSubmit } from './hooks/useOnSubmit'
import { useTexts } from './hooks/useTexts'
import type { DriverFormValues } from './types'
import { computeFormInitialValues } from './utils/computeFormInitialValues'
import { findFirstEligibleGpsDeviceId } from './utils/findFistEligibleGpsDeviceId'

type DriverFormProps = {
  driver?: uui.domain.client.rm.ExtendedDriver
}

export function DriverForm(props: DriverFormProps) {
  const { driver } = props
  const {
    header,
    form: { driverAppTitle },
  } = useTexts()
  const initialDeviceId = driver?.type === 'linked' ? driver.device.deviceId : undefined
  const gpsDeviceOptions = useGpsDeviceIdOptions(initialDeviceId)
  const firstEligibleGpsDeviceId = findFirstEligibleGpsDeviceId(gpsDeviceOptions) ?? ''
  const trackingProvider = useSelector(selectGpsTrackingProvider)

  const config = useMemo<FormConfig<DriverFormValues>>(
    () => ({
      id: 'driver-form',
      initialValues: computeFormInitialValues(
        trackingProvider,
        gpsDeviceOptions,
        driver,
        firstEligibleGpsDeviceId,
      ),
    }),
    [driver],
  )

  const creating = config.initialValues.id === emptyDriverId

  const onSubmit = useOnSubmit()
  const title = config.initialValues.name || header.newDriver

  return (
    <form.Form
      rightBorder
      title={title}
      config={config}
      onSubmit={onSubmit}
      requireTransaction
      stopEditingOnUnmount
      applyPendingFitMapOnMount={false}
      footer={<Footer />}
    >
      <Name />
      <Email />
      {creating && <Password />}
      <form.Divider />
      <form.SectionTitle>{driverAppTitle}</form.SectionTitle>
      <TrackDriver gpsDeviceOptions={gpsDeviceOptions} />
      <BarcodeAttachment />
      <MobileFutureDaysVisibility />
      <SummarizeFutureMobileRoute />
    </form.Form>
  )
}
